<template>
  <div
    ref="canvas-wrapper-ref"
    id="editor-bound"
    class="canvas-wrapper space-y-4 canvas-bg-color"
    :class="[isPreview ? 'preview-only' : 'no-preview']"
    :style="[customCss]"
  >
    <div
      v-for="(section, index) of sections"
      :key="index"
      class="w-full rounded-lg shadow-lg"
    >
      <section-wrapper
        class="section-bg-color relative"
        :isPreview="isPreview"
        :index="index"
        :length="sections.length"
        :deleteSection="deleteSection"
        :moveSection="moveSection"
        :cloneSection="cloneSection"
        v-if="
          !section.type.includes('download-component') ||
          (section.type.includes('download-component') &&
            (!section.section_content.hasOwnProperty('showInSidebar') ||
              (section.section_content.hasOwnProperty('showInSidebar') &&
                !section.section_content.showInSidebar)))
        "
      >
        <template slot="title">
          <title-wrapper class="section-heading-color" v-if="!isPreview">
            <quill-editor
              :value="section.name_markup"
              :options="getQuillEditorOptions('section-heading')"
              @change="updateSectionTitle($event, index)"
              @ready="onEditorReady($event)"
            ></quill-editor>
          </title-wrapper>
          <title-wrapper
            v-else
            class="section-heading-color"
            v-html="section.name_markup"
          >
          </title-wrapper>
        </template>
        <template slot="content">
          <content-wrapper class="section-content-color">
            <div v-if="section.type === 'video-component'">
              <video-content
                :isPreview="isPreview"
                :index="index"
                :getThemeSettings="getThemeSettings"
                :content="section.section_content"
                @updateSectionContent="updateSectionContent($event, index)"
              >
              </video-content>
            </div>
            <div v-if="section.type === 'content-component'">
              <text-content
                :isPreview="isPreview"
                :index="index"
                :getThemeSettings="getThemeSettings"
                :content="section.section_content"
                @updateSectionContent="updateSectionContent($event, index)"
              >
              </text-content>
            </div>
            <div v-if="section.type === 'checklist-component'">
              <list-content
                :isPreview="isPreview"
                :index="index"
                :getThemeSettings="getThemeSettings"
                :content="section.section_content"
                @updateSectionContent="updateSectionContent($event, index)"
              >
              </list-content>
            </div>
            <div v-if="section.type === 'accordion-component'">
              <accordion-content
                :isPreview="isPreview"
                :index="index"
                :getThemeSettings="getThemeSettings"
                :content="section.section_content"
                @updateSectionContent="updateSectionContent($event, index)"
              >
              </accordion-content>
            </div>
            <div v-if="section.type === 'download-component'">
              <download-content
                :isPreview="isPreview"
                :index="index"
                :getThemeSettings="getThemeSettings"
                :files="files"
                :filesCategory="filesCategory"
                :content="section.section_content"
                @updateSectionContent="updateSectionContent($event, index)"
              >
              </download-content>
            </div>
            <div v-if="section.type === 'audio-component'">
              <audio-content
                :isPreview="isPreview"
                :index="index"
                :getThemeSettings="getThemeSettings"
                :files="files.filter((file) => file.mime_type === 'audio/mpeg')"
                :content="section.section_content"
                @updateSectionContent="updateSectionContent($event, index)"
              >
              </audio-content>
            </div>
            <div v-if="section.type === 'pdf-component'">
              <pdf-content
                :isPreview="isPreview"
                :index="index"
                :getThemeSettings="getThemeSettings"
                :files="
                  files.filter((file) => file.mime_type === 'application/pdf')
                "
                :content="section.section_content"
                @updateSectionContent="updateSectionContent($event, index)"
              >
              </pdf-content>
            </div>
          </content-wrapper>
        </template>
      </section-wrapper>
    </div>
    <add-section-button
      v-if="!isPreview"
      :callback="addNewSection"
      :length="sections.length"
    >
    </add-section-button>
  </div>
</template>
<script>
import quillMixin from '../../mixins/quill';
import SectionWrapper from './wrapper/section';
import TitleWrapper from './wrapper/title';
import ContentWrapper from './wrapper/content';
import VideoContent from './content/video';
import TextContent from './content/just-text';
import ListContent from './content/checklist';
import AccordionContent from './content/accordion';
import DownloadContent from './content/download';
import AudioContent from './content/audio';
import PdfContent from './content/pdf';
import AddSectionButton from './button/add-section';

export default {
  name: 'Canvas',
  mixins: [quillMixin],
  data() {
    return {};
  },
  props: {
    isPreview: {
      type: Boolean,
      default: true,
    },
    files: {},
    filesCategory: {},
    getThemeSettings: {
      type: Function,
      required: true,
    },
    getSections: {
      type: Function,
      required: true,
    },
    addNewSection: {
      type: Function,
      required: true,
    },
    deleteSection: {
      type: Function,
      required: true,
    },
    moveSection: {
      type: Function,
      required: true,
    },
    cloneSection: {
      type: Function,
      required: true,
    },
    updateSectionTitle: {
      type: Function,
      required: true,
    },
    updateSectionContent: {
      type: Function,
      required: true,
    },
  },
  components: {
    SectionWrapper,
    TitleWrapper,
    ContentWrapper,
    VideoContent,
    TextContent,
    ListContent,
    AccordionContent,
    DownloadContent,
    AudioContent,
    PdfContent,
    AddSectionButton,
  },
  computed: {
    customCss() {
      return {
        '--template-canvas-bg-color':
          this.themeSettings.configuration_options.canvas_bg_color,
        '--template-section-bg-color':
          this.themeSettings.configuration_options.section_bg_color,
        '--template-section-heading-color':
          this.themeSettings.configuration_options.section_heading_color,
        '--template-section-content-color':
          this.themeSettings.configuration_options.section_content_color,
      };
    },
    sections() {
      return this.getSections();
    },
    themeSettings() {
      return this.getThemeSettings();
    },
  },
  methods: {},
  watch: {},
};
</script>

<style lang="scss" scoped>
.canvas-bg-color {
  background-color: var(--template-canvas-bg-color);
}
.section-bg-color {
  background-color: var(--template-section-bg-color);
}
.section-heading-color {
  color: var(--template-section-heading-color);
}
.section-content-color {
  color: var(--template-section-content-color);
}
</style>

<style lang="scss">
@import '../../../../assets/css/campton_font.css';

.canvas-wrapper {
  ol {
    list-style: numeric;
    padding-left: 1.5rem;
  }
  ul {
    list-style: disc;
    padding-left: 1.5rem;
  }
  .custom-delete-btn {
    position: absolute;
    right: -36px;
    margin-top: 20px;
    cursor: pointer;
    &::before {
      width: 32px;
      height: 32px;
      background: url('../../../../assets/images/icons/round-delete.png');
      display: inline-block;
      content: '';
      outline: none;
    }
  }
  .add-button {
    margin: 20px 0 0;
    display: inline-block;
    border-radius: 6px;
    color: #fff !important;
    font-size: 16px;
    font-weight: bold;
    pointer-events: none;
    position: relative;
    &::before,
    &::after {
      content: none !important;
    }
    &.align-left {
      left: 0;
      transform: none;
    }
    &.align-right {
      left: 100%;
      transform: translateX(-100%);
    }
    &.align-center {
      left: 50%;
      transform: translateX(-50%);
    }
    a {
      cursor: pointer;
      display: block;
      text-decoration: none !important;
      padding: 10px 40px;
      border-radius: 6px;
      text-align: center;
    }
  }
}

.canvas-wrapper.preview-only {
  ol {
    list-style: numeric;
    padding-left: 3rem;
  }
  ul {
    list-style: disc;
    padding-left: 3rem;
  }
  .custom-delete-btn {
    display: none;
  }
  .add-button {
    pointer-events: all;
  }
}

.membership-template-builder {
  h1 {
    font-size: 42px;
  }
  h2 {
    font-size: 36px;
  }
  h3 {
    font-size: 30px;
  }
  h4 {
    font-size: 24px;
  }
  p {
    font-size: 18px;
    word-break: break-word;
  }
  strong {
    font-family: 'Campton Bold', sans-serif;
    font-weight: normal;
  }

  .ql-tooltip {
    z-index: 50;
    .ql-toolbar {
      background: #292c49;
      -webkit-box-shadow: 0px 3px 2px rgba(0, 0, 0, 0.2);
      box-shadow: 0px 3px 2px rgba(0, 0, 0, 0.2);
      display: flex;
      border-radius: 50px;
    }
    .ql-tooltip-arrow {
      border-bottom-color: #292c49;
    }
  }
  // .ql-container {
  //   font-family: 'Nunito Sans', sans-serif;
  // }
  .ql-container,
  .campton-font {
    font-family: 'Campton Book', sans-serif;
    font-weight: normal;
    strong,
    .font-bold,
    &.font-bold {
      font-family: 'Campton Bold', sans-serif;
      font-weight: normal;
    }
  }
  // .membership-content-section .ql-container,
  // .hierarchy-bg-color {
  //   font-family: 'Campton Book', sans-serif;
  //   font-weight: normal;
  //   strong,
  //   .font-bold {
  //     font-family: 'Campton Bold', sans-serif;
  //     font-weight: normal;
  //   }
  // }
  iframe {
    &.ql-video,
    &.custom-ql-video,
    &.custom-embed-iframe {
      width: 100%;
      height: 400px;
      margin: 20px 0 0;
      max-width: 100%;
    }
  }
  .membership-content-section .video-iframe-wrapper {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    iframe {
      &.ql-video,
      &.custom-ql-video,
      &.custom-embed-iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        margin: 0;
      }
    }
  }
  .custom-ql-image {
    margin: 20px 0 0;
  }
  .ql-bubble {
    .ql-editor {
      padding: 0;
      outline: none;
      h1 {
        font-size: 42px;
      }
      h2 {
        font-size: 36px;
        line-height: 1.1;
      }
      h3 {
        font-size: 30px;
      }
      h4 {
        font-size: 24px;
      }
      p,
      ul,
      ol {
        font-size: 18px;
      }
    }
    /** */
  }
  .ql-bubble .ql-picker.ql-header .ql-picker-label[data-value='2']::before,
  .ql-bubble .ql-picker.ql-header .ql-picker-item[data-value='2']::before {
    content: 'Heading';
  }
  .ql-bubble .ql-picker.ql-header .ql-picker-label[data-value='3']::before,
  .ql-bubble .ql-picker.ql-header .ql-picker-item[data-value='3']::before {
    content: 'Sub Heading';
  }
  .ql-bubble .ql-picker.ql-header .ql-picker-label::before,
  .ql-bubble .ql-picker.ql-header .ql-picker-item::before {
    content: 'Paragraph';
  }
  .view-icons {
    .icon {
      border: 1px solid #dcdcea;
      border-radius: 2px;
    }
    svg {
      color: #dcdcea;
    }
    .active-icon {
      border-color: #6993ee;
      svg {
        color: #6993ee;
      }
    }
  }
  .mobile-template {
    width: 400px;
    .courses-outer-wrapper > .flex-col {
      width: 100%;
    }
    .view-icons {
      display: none;
    }
    .other-course-inner {
      flex-direction: column;
      & > .h-40 {
        width: 100%;
        height: 160px;
      }
    }

    .membership-content-section {
      padding: 24px !important;
    }
    .ql-bubble {
      .ql-editor {
        padding: 0 0;
        h1 {
          font-size: 32px;
        }
        h2 {
          font-size: 28px;
        }
        h3 {
          font-size: 24px;
        }
        h4 {
          font-size: 20px;
        }
        p {
          font-size: 16px;
        }
      }
    }
    .checklist-type-change {
      display: none;
      & + .flex {
        flex-direction: column;
        & > .flex {
          width: 100%;
        }
      }
    }
    .section-paragraph > .text-pink.text-xl {
      font-size: 14px;
    }

    .membership-layout-container.membership-lesson-container {
      display: block;
      .md\:w-8\/12 {
        width: 100%;
      }
      .md\:w-4\/12 {
        width: 100%;
        margin: 20px 0 0;
        padding-bottom: 20px;
      }
      // & > .flex {
      //   display: block;
      //   & > .membership-layout-container {
      //     width: 100%;
      //   }
      //   & > .ml-4 {
      //     width: 100%;
      //     margin: -50px 0 0;
      //     padding-bottom: 20px;
      //   }
      // }
      &.lesson-breadcrumb {
        display: flex;
        flex-wrap: wrap;
      }
    }
    .tile-wrapper {
      flex-direction: column;
      .w-full.h-40 {
        width: 100%;
        height: 160px;
      }
    }
    .footer-menu {
      flex-direction: column;
      .h-10 {
        margin-bottom: 5px;
      }
      .flex.text-base {
        flex-direction: column;
      }
      .footer-text-color {
        margin: 10px 0 0;
        text-align: center;
      }
    }
    .menu-icon {
      display: block;
    }
    .menu-btn:not(:checked) {
      & ~ .menu {
        display: none;
      }
      & ~ .menu-icon {
        .fa-bars {
          display: block !important;
        }
        .fa-times {
          display: none !important;
        }
      }
    }
    .menu-btn:checked {
      & ~ .menu {
        width: 100%;
        display: block;
        position: absolute;
        left: 0;
        top: 100%;
        z-index: 99;
        padding: 0 15px 10px;
        .header-text-color {
          padding: 0 0 10px;
          text-align: center;
        }
        .h-full.static {
          display: none;
        }
        button {
          margin: 0 auto;
        }
      }
      & ~ .menu-icon {
        .fa-bars {
          display: none !important;
        }
        .fa-times {
          display: block !important;
        }
      }
    }
    .video-wrapper iframe {
      height: 250px;
    }
    .custom-scrollbars {
      overflow-x: hidden;
    }
  }
  .tablet-template {
    width: 768px;
    .courses-outer-wrapper > .flex-col {
      width: 50%;
    }

    .membership-content-section {
      padding: 24px !important;;
    }
    .ql-bubble {
      .ql-editor {
        padding: 0 0;
        h1 {
          font-size: 38px;
        }
        h2 {
          font-size: 34px;
        }
        h3 {
          font-size: 30px;
        }
        h4 {
          font-size: 24px;
        }
        p {
          font-size: 16px;
        }
      }
    }
    .membership-layout-container .checklist-type-change {
      right: 17px;
      top: 6px;
    }
    .membership-layout-container.membership-lesson-container
      .checklist-type-change {
      right: 17px;
      top: 13px;
    }
    .menu .static {
      position: static;
    }
    // .full-tile-hover.w-full.flex-row .flex-1 .absolute.left-0 {
    //   left: 33.333333%;
    // }
    .video-wrapper iframe {
      height: 300px;
    }
    .hierarchy-box .accordion-sidebar-height {
      padding: 0;
    }
  }
  .desktop-template {
    width: 100%;
    // .full-tile-hover.w-full.flex-row .flex-1 .absolute.left-0 {
    //   left: 33.333333%;
    // }
  }
  .builder-logo {
    .text-sm {
      font-size: 14px;
    }
    .text-xs {
      font-size: 12px;
    }
  }
}

// .quill-editor .ql-toolbar {
//  background:#292c49;
//  -webkit-box-shadow:0 3px 2px rgba(0,0,0,.2);
//  box-shadow:0 3px 2px rgba(0,0,0,.2);
//  display:-webkit-box;
//  display:-ms-flexbox;
//  display:flex;
//  border-radius:50px
// }

@media only screen and (max-width: 1023px) {
  .membership-template-builder {
    .membership-content-section {
      padding-left: 40px;
      padding-right: 40px;
    }
    .ql-bubble {
      .ql-editor {
        padding: 0 0;
        h1 {
          font-size: 38px;
        }
        h2 {
          font-size: 34px;
        }
        h3 {
          font-size: 30px;
        }
        h4 {
          font-size: 24px;
        }
        p {
          font-size: 16px;
        }
      }
    }
    .checklist-type-change {
      right: -8px;
    }
  }
  .membership-template-builder
    .membership-layout-container.membership-lesson-container
    .membership-content-section {
    padding: 24px;
  }
}

@media only screen and (max-width: 767px) {
  .membership-template-builder {
    .membership-content-section {
      padding: 24px;
    }
    .ql-bubble {
      .ql-editor {
        padding: 0 0;
        h1 {
          font-size: 32px;
        }
        h2 {
          font-size: 28px;
        }
        h3 {
          font-size: 24px;
        }
        h4 {
          font-size: 20px;
        }
        p {
          font-size: 16px;
        }
      }
    }
    .membership-layout-container.membership-lesson-container
      .membership-content-section {
      padding: 24px;
    }
  }
  .membership-site-header {
    .menu-btn:not(:checked) {
      & ~ .menu {
        display: none;
      }
      & ~ .menu-icon {
        margin-bottom: 0px !important;
        .fa-bars {
          display: block !important;
        }
        .fa-times {
          display: none !important;
        }
        svg,
        label {
          margin-bottom: 0px !important;
        }
      }
    }
    .menu-btn:checked {
      & ~ .menu {
        width: 100%;
        display: block;
        position: absolute;
        left: 0;
        top: 100%;
        z-index: 50;
        padding: 0 15px 10px;
        .header-text-color {
          padding: 0 0 10px;
          text-align: center;
        }
        .h-full.static {
          display: none;
        }
      }
      & ~ .menu-icon {
        .fa-bars {
          display: none !important;
        }
        .fa-times {
          display: block !important;
        }
      }
    }
  }
}
.arrow-grey {
  color: #c8cbd8;
}
.custom-scrollbars.removeheight {
  overflow: hidden;
}
.steps-wrapper {
  overflow: scroll;
}

</style>